import { resolveVideoPublisherDisplay } from "../../../domains/content/videoPublisher";
import type { Api } from "../../../services/api";
import {
  AnalyticsItemCategory,
  AnalyticsItemVariant,
} from "../analytics.types";
import { gaPushEvent } from "../base.analytics";
import {
  AnalyticsItemReturnData,
  gaEventSelectItem,
  gaEventViewItem,
  gaEventViewItemList,
} from "../common.analytics";
import type { CongressAttribution } from "../../../domains/congress/congressAttribution";

enum Event {
  VIDEO_PLAYED = "video_played",
  VIDEO_LIKE = "video_like",
  VIDEO_SAVE = "video_save",
  VIDEO_SHARE = "video_share",
}

type VideoDetail = Extract<
  Api["get"]["/webapp/public/content/:id"]["output"]["body"],
  { content_format: "Video" }
>;
type VideoListItem = Extract<
  Api["get"]["/webapp/public/content"]["output"]["body"]["docs"][number],
  { content_format: "Video" }
>;
type RoomVideoItem = Extract<
  Api["get"]["/webapp/private/content/last-in-user-room"]["output"]["body"][number],
  { content_format: "Video" }
>;

type VideoAnalyticsData = VideoListItem | VideoDetail | RoomVideoItem;

type VideoItemPayload = {
  video: VideoAnalyticsData;
  listName?: string;
};

/** Extract top-level Mixpanel properties from a video model. */
function getVideoTopLevelProps(video: VideoAnalyticsData) {
  return {
    language: "language" in video ? video.language : undefined,
  };
}

type VideoItemListPayload = {
  videos: VideoAnalyticsData[];
  listName: string;
};

export function convertVideoToItemData(
  payload: VideoItemPayload
): AnalyticsItemReturnData {
  const { video, listName } = payload;
  const isStory = video.videoFormat === "story";
  return {
    item_id: video.id,
    item_name: video.title,
    item_brand: resolveVideoPublisherDisplay(video).name,
    item_category: video.medical_specialties.map((spe) => spe.uid).join(","),
    item_category2: AnalyticsItemCategory.STANDARD,
    item_variant: isStory
      ? AnalyticsItemVariant.VIDEO_STORY
      : AnalyticsItemVariant.VIDEO_ARTICLE,
    item_list_name: listName,
    language: "language" in video ? video.language : undefined,
  };
}

export function gaEventSelectItemVideo(payload: VideoItemPayload) {
  gaEventSelectItem(convertVideoToItemData(payload));
}

export function gaEventViewItemVideo(
  payload: VideoItemPayload,
  congress: CongressAttribution | null
) {
  gaEventViewItem(convertVideoToItemData(payload), congress);
}

export function gaEventViewItemListVideo(payload: VideoItemListPayload) {
  gaEventViewItemList(
    payload.videos.map((video) => {
      return convertVideoToItemData({
        video,
        listName: payload.listName,
      });
    })
  );
}

export function gaEventVideoPlay(payload: VideoItemPayload) {
  const props = getVideoTopLevelProps(payload.video);
  gaPushEvent(Event.VIDEO_PLAYED, {
    ...props,
    ecommerce: {
      items: [convertVideoToItemData(payload)],
    },
  });
}

export function gaEventVideoLike(payload: VideoItemPayload) {
  const props = getVideoTopLevelProps(payload.video);
  gaPushEvent(Event.VIDEO_LIKE, {
    ...props,
    ecommerce: {
      items: [convertVideoToItemData(payload)],
    },
  });
}

export function gaEventVideoSave(payload: VideoItemPayload) {
  const props = getVideoTopLevelProps(payload.video);
  gaPushEvent(Event.VIDEO_SAVE, {
    ...props,
    ecommerce: {
      items: [convertVideoToItemData(payload)],
    },
  });
}

export function gaEventVideoShare(payload: VideoItemPayload) {
  const props = getVideoTopLevelProps(payload.video);
  gaPushEvent(Event.VIDEO_SHARE, {
    ...props,
    ecommerce: {
      items: [convertVideoToItemData(payload)],
    },
  });
}
